import React, { Component } from "react";
import { connect } from "react-redux";
import { ProgressBar, Spinner } from "react-bootstrap";
import "./styles.css";
import { setUploadProgress, setUploadState } from "../actions";
import { getRawUploadSignedURL, putFileS3XML, startParsing } from "../helpers";
import { EXCLUDE_FILE_LIST, uploadStates } from "../constants";
import { vsprintf } from "sprintf-js";
import { addPendoTracking, traverseDirectory } from "../../../helpers/helpers";
import { clientErrorMessages, EMPTY_STRING } from "../../../constants";
import { PropTypes } from "prop-types";
import { filePathFilter } from "@jsdevtools/file-path-filter";
import {
    getAbortController,
    resetAbortController,
} from "../../__shared__/AbortController/AbortController";
import { trackingEventNames } from "../../../helpers/pendo_constants";

class UploadProgressCard extends Component {
    constructor(props) {
        super(props);

        this.processFile = this.processFile.bind(this);
        this.processDirectory = this.processDirectory.bind(this);
        this.uploadFile = this.uploadFile.bind(this);
        this.uploadDirectory = this.uploadDirectory.bind(this);
        this.uploadFiles = this.uploadFiles.bind(this);
    }

    componentDidMount() {
        resetAbortController();

        if (this.props.folder !== null) {
            this.processDirectory();
        } else if (this.props.file !== null) {
            this.processFile();
        }
    }

    componentWillUnmount() {
        this.props.setUploadProgress(0);
        let abortController = getAbortController();
        abortController.abort();
    }

    processFile() {
        let scope = this;
        let abortController = getAbortController();
        let signal = abortController.signal;

        new Promise(
            () => {
                if (scope.props.file) {
                    if (scope.props.file.name) {
                        scope.uploadFile(scope.props.file);
                    } else {
                        let file = scope.props.file.getAsFile();
                        scope.uploadFile(file);
                    }
                }
            },
            { signal }
        );
    }

    uploadFile(file) {
        let scope = this;
        let isDir = false;
        let key = vsprintf("%s/%s/%s/%s/%s/%s", [
            scope.props.useruuid,
            scope.props.patientuuid,
            scope.props.planuuid,
            scope.props.uploadIteration,
            scope.props.label,
            file.name,
        ]);

        getRawUploadSignedURL(scope.props.authToken, key, scope.props.planuuid)
            .then((res) => {
                putFileS3XML(res.signedurl, file, scope.props.setUploadProgress)
                    .then(() => {
                        scope.props.uploadCallback(true);
                        scope.props.setFileKey(res.filekey);

                        if (
                            !startParsing({
                                label: scope.props.label,
                                token: scope.props.authToken,
                                useruuid: scope.props.useruuid,
                                patientuuid: scope.props.patientuuid,
                                planuuid: scope.props.planuuid,
                                visituuid: scope.props.visituuid,
                                queuetag: String(scope.props.uploadIteration),
                                filekey: res.filekey,
                                isdir: false,
                                isFreehand: this.props.isFreehand,
                            }).catch((e) => {
                                this.handleParsingError(e, isDir);
                            })
                        ) {
                            this.handleParsingError(
                                clientErrorMessages.PARSING_START_FAILED,
                                isDir
                            );
                        }

                        let abortController = getAbortController();
                        let signal = abortController.signal;

                        if (!signal.aborted) {
                            scope.props.setUploadState(uploadStates.PARSING);
                        }
                    })
                    .catch((e) => {
                        this.handleParsingError(e, isDir);
                    });
            })
            .catch((e) => {
                this.handleParsingError(e, isDir);
            });
    }

    handleParsingError(e, isDir) {
        const otherData = {
            label: this.props.label,
            patientuuid: this.props.patientuuid,
            visituuid: this.props.visituuid,
            queuetag: String(this.props.uploadIteration),
            isDirectory: isDir,
            isFreehand: this.props.isFreehand,
        };
        addPendoTracking(
            trackingEventNames.PARSING_NEW_PATIENT,
            this.props.planuuid,
            clientErrorMessages.PARSING_FAILURE_MESSAGE_PREFIX + String(e),
            otherData
        );
        this.props.setUploadState(uploadStates.FAILED);
    }

    processDirectory() {
        let scope = this;

        let abortController = getAbortController();
        let signal = abortController.signal;

        new Promise(
            () => {
                let item;
                const fileEntryList = [];

                if (this.props.folder.webkitGetAsEntry) {
                    item = this.props.folder.webkitGetAsEntry();
                    traverseDirectory(item, fileEntryList).then(() => {
                        scope.uploadFiles(fileEntryList);
                    });
                } else {
                    item = this.props.folder;
                    this.uploadDirectory(item);
                }
            },
            { signal }
        );
    }

    uploadFiles(fileEntryList) {
        let numFilesProcessed = 0;
        let filesToUpload = [];
        let scope = this;

        fileEntryList.forEach((fileEntry) => {
            fileEntry.file(function (file) {
                filesToUpload.push([fileEntry.fullPath, file]);
                numFilesProcessed++;

                if (numFilesProcessed === fileEntryList.length) {
                    scope.uploadDirectory(filesToUpload);
                }
            });
        });
    }

    filterJunkFiles(filesToUpload) {
        let filteredList = [];
        filesToUpload.forEach((entry) => {
            let t = entry.filter(
                filePathFilter({ include: EXCLUDE_FILE_LIST })
            );
            if (!t.length > 0) {
                filteredList.push(entry);
            }
        });

        return filteredList;
    }

    getTotalUploadSize(files) {
        let totalUploadSize = 0;

        files.forEach((file) => {
            if (file[1]) {
                totalUploadSize += file[1].size;
            }
        });

        return totalUploadSize;
    }

    uploadDirectory(filesToUpload) {
        let scope = this;
        let numFilesUploaded = 0;
        let numBytesUploaded = 0;

        let filteredUploadFiles = this.filterJunkFiles(filesToUpload);

        let totalUploadSize = this.getTotalUploadSize(filteredUploadFiles);

        filteredUploadFiles.forEach((entry) => {
            if (entry.length >= 2) {
                let filePath = entry[0];
                let file = entry[1];
                let filePathSegment = filePath.startsWith("/")
                    ? filePath.substring(1)
                    : filePath;

                filePathSegment = encodeURIComponent(filePathSegment);

                let key = vsprintf("%s/%s/%s/%s/%s/%s", [
                    scope.props.useruuid,
                    scope.props.patientuuid,
                    scope.props.planuuid,
                    scope.props.uploadIteration,
                    scope.props.label,
                    filePathSegment,
                ]);

                getRawUploadSignedURL(scope.props.authToken, key)
                    .then((res) => {
                        putFileS3XML(res.signedurl, file)
                            .then(() => {
                                scope.props.setUploadProgress(
                                    (numBytesUploaded / totalUploadSize) * 100
                                );
                                numBytesUploaded += file.size;
                                numFilesUploaded++;

                                if (
                                    numFilesUploaded ===
                                    filteredUploadFiles.length
                                ) {
                                    scope.props.uploadCallback(true);

                                    let folderKey = vsprintf("%s/%s/%s/%s/%s", [
                                        scope.props.useruuid,
                                        scope.props.patientuuid,
                                        scope.props.planuuid,
                                        scope.props.uploadIteration,
                                        scope.props.label,
                                    ]);

                                    scope.props.setFileKey(folderKey);
                                    scope.props.setFolderKey(folderKey);

                                    startParsing({
                                        label: scope.props.label,
                                        token: scope.props.authToken,
                                        useruuid: scope.props.useruuid,
                                        patientuuid: scope.props.patientuuid,
                                        planuuid: scope.props.planuuid,
                                        visituuid: scope.props.visituuid,
                                        queuetag: String(
                                            scope.props.uploadIteration
                                        ),
                                        filekey: folderKey,
                                        isdir: true,
                                        isFreehand: this.props.isFreehand,
                                    }).catch((e) => {
                                        this.handleParsingError(e, true);
                                    });

                                    let abortController = getAbortController();
                                    let signal = abortController.signal;

                                    if (!signal.aborted) {
                                        scope.props.setUploadState(
                                            uploadStates.PARSING
                                        );
                                    }
                                }
                            })
                            .catch((e) => {
                                this.handleParsingError(e, true);
                            });
                    })
                    .catch((e) => {
                        this.handleParsingError(e, true);
                    });
            } else {
                this.handleParsingError(
                    clientErrorMessages.PARSING_START_FAILED,
                    true
                );
            }
        });
    }

    isValidBiopsyDataFile(item) {
        let nameLower = item.name.toLowerCase();
        return (
            nameLower.endsWith(".vtk") ||
            nameLower.endsWith(".dcm") ||
            nameLower.endsWith(".xml") ||
            nameLower.endsWith(".bmp") ||
            nameLower.endsWith(".txt") ||
            nameLower === "seg"
        );
    }

    render() {
        return (
            <div
                id={this.props.id}
                className={`upload-card progress solid`}
                style={this.props.style}
            >
                <div className={"finish-check-container"}>
                    <Spinner animation="grow" variant="primary" />
                </div>
                <div className={"upload-bar-container"}>
                    <div className={"upload-progress-bar"}>
                        <ProgressBar
                            animated
                            now={this.props.uploadProgress}
                            label={`Uploading File... ${this.props.uploadProgress}%`}
                        />
                    </div>
                    <div className={"parse-progress-bar"}>
                        <ProgressBar
                            animated
                            now={0}
                            label={`Reading File... ${0}%`}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

UploadProgressCard.propTypes = {
    file: PropTypes.object,
    id: PropTypes.string,
    style: PropTypes.object,
    useruuid: PropTypes.string,
    authToken: PropTypes.string,
    planuuid: PropTypes.string,
    visituuid: PropTypes.string,
    patientuuid: PropTypes.string,
    uploadIteration: PropTypes.number,
    uploadProgress: PropTypes.string,
    label: PropTypes.string,
    folder: PropTypes.object,
    setUploadProgress: PropTypes.func,
    setUploadState: PropTypes.func,
    isFreehand: PropTypes.bool,
};

UploadProgressCard.defaultProps = {
    file: null,
    folder: null,
    id: EMPTY_STRING,
    style: null,
    useruuid: EMPTY_STRING,
    authToken: EMPTY_STRING,
    planuuid: EMPTY_STRING,
    visituuid: EMPTY_STRING,
    patientuuid: EMPTY_STRING,
    uploadIteration: 0,
    uploadProgress: "0",
    label: EMPTY_STRING,
    isFreehand: false,
};

/* istanbul ignore next */
const mapStateToProps = function (state, ownProps) {
    return {
        useruuid: state.LoginReducer.useruuid,
        authToken: state.LoginReducer.authToken,
        planuuid: state.CreatePlanReducer.planuuid,
        visituuid: state.CreatePlanReducer.visituuid,
        patientuuid: state.CreatePlanReducer.patientuuid,
        uploadProgress: state.CreatePlanReducer.uploadProgress[ownProps.idx],
    };
};

/* istanbul ignore next */
const mapDispatchToProps = function (dispatch, ownProps) {
    return {
        setUploadState: (upstate) =>
            dispatch(setUploadState(ownProps.idx, upstate)),
        setUploadProgress: (prog) =>
            dispatch(setUploadProgress(ownProps.idx, prog)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadProgressCard);
